/**
 * idbCorruptionTracker — single global registry of in-flight IndexedDB
 * loads, shared by every local-first sync service (crdtServiceWS's
 * space:/space-chat:/doc:/note:/terminal: rooms AND localFirstSyncService's
 * per-entity docs: Page/Transcript/Diagram/Table/Chart/SkillWizard/
 * VideoProject/SocialPost/ImageProject/Mail.Draft/generic
 * template entities).
 *
 * y-indexeddb replays persisted updates through an internal promise chain
 * with no `.catch()` anywhere in the library; a malformed/truncated stored
 * update throws inside that chain, which never surfaces as a rejection on
 * `whenSynced` or an 'error' event — the only observable signal is a global
 * `unhandledrejection`, and there's no way to recover which room caused it
 * from the rejection itself. The only way to correlate it back to a room is
 * to track which IDB loads are currently in flight and — when exactly one
 * candidate is loading — treat that as the corrupt one.
 *
 * That correlation is only safe if EVERY service's in-flight loads share
 * ONE map. If each service tracked its own loads separately, a corrupt load
 * owned by one service could be misattributed to an unrelated, healthy room
 * owned by the other whenever that other service happened to have exactly
 * one load of its own in flight at the same moment — silently wiping a
 * perfectly good local cache instead of the actually-corrupt one.
 */
import { clearCorruptIdb, isCorruptDecodeError, type ClearableIdb } from './idbBloatDetector';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('IDB_CORRUPTION_TRACKER', { enabled: true });

// How long to wait for sibling loads to self-resolve before giving up on
// correlating a corrupt-decode rejection. Opening a Space loads its
// `space:` room plus one `doc:`/`note:`/`terminal:`/entity room per open
// window in the same synchronous batch, so 2+ candidates in flight at once
// is the norm, not evidence that correlation is genuinely impossible.
// Non-corrupt siblings are typically small (or already warm in the
// browser's IDB cache) and self-remove within a few milliseconds. Waiting a
// short, bounded window lets those siblings clear out so the truly-stuck
// (corrupt) load is left as the sole candidate — without ever weakening the
// safety property: if 2+ candidates are STILL present once the window
// elapses, we still refuse to guess.
const CORRELATION_GRACE_MS = 300;
const CORRELATION_POLL_MS = 25;

const idbLoading = new Map<string, ClearableIdb>();
const subscribers = new Set<(key: string, idb: ClearableIdb, reason: string) => void>();

/** Registers a load as "in flight" — call as soon as the IndexeddbPersistence is created. */
export function trackIdbLoad(key: string, idb: ClearableIdb): void {
  idbLoading.set(key, idb);
}

/** Deregisters a load — call on its 'synced' event, and on any manual teardown/disconnect. */
export function untrackIdbLoad(key: string): void {
  idbLoading.delete(key);
}

/**
 * Registers a subscriber notified whenever a tracked load is confirmed
 * corrupt and its IDB store has been cleared. Subscribers must check
 * ownership of `key` themselves (e.g. via their own doc/provider map)
 * before reacting — this tracker has no concept of which service owns
 * which key. Returns an unsubscribe function.
 */
export function onIdbCorruptionCleared(
  callback: (key: string, idb: ClearableIdb, reason: string) => void,
): () => void {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

/**
 * Resolves to the sole load currently in flight, or `null` if that can't be
 * determined safely (zero candidates, or 2+ candidates still remain after a
 * brief grace window for siblings to self-resolve). Never guesses among
 * genuinely-ambiguous candidates — wrongly clearing a sibling's IDB would
 * destroy real offline edits it may hold.
 */
function resolveCorruptCandidate(
  graceMs: number = CORRELATION_GRACE_MS,
): Promise<[string, ClearableIdb] | null> {
  if (idbLoading.size === 1) {
    return Promise.resolve([...idbLoading.entries()][0]);
  }
  if (idbLoading.size === 0) {
    return Promise.resolve(null);
  }

  const deadline = Date.now() + graceMs;
  return new Promise((resolve) => {
    const check = () => {
      if (idbLoading.size === 1) {
        resolve([...idbLoading.entries()][0]);
        return;
      }
      if (idbLoading.size === 0 || Date.now() >= deadline) {
        resolve(null);
        return;
      }
      setTimeout(check, CORRELATION_POLL_MS);
    };
    check();
  });
}

function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  const rejectionReason = event.reason;
  const message = rejectionReason instanceof Error ? rejectionReason.message
    : typeof rejectionReason === 'string' ? rejectionReason : undefined;
  if (!isCorruptDecodeError(message)) return;

  const candidateCountAtDetection = idbLoading.size;
  void resolveCorruptCandidate().then((candidate) => {
    if (!candidate) {
      log.warn(
        `[IDB_CORRUPTION] Corrupt-IDB-shaped rejection detected but correlation is ambiguous ` +
        `(${candidateCountAtDetection} loads at detection, ${idbLoading.size} after grace window) ` +
        `— not clearing. message="${message}"`,
      );
      return;
    }

    const [key, idb] = candidate;
    idbLoading.delete(key);
    const corruptReason = message ?? 'unknown decode error';
    log.error(`[IDB_CORRUPTION] Corrupt IndexedDB detected for ${key.slice(-8)}: ${corruptReason}`);

    void clearCorruptIdb(key, idb, corruptReason).then(() => {
      for (const subscriber of subscribers) {
        try {
          subscriber(key, idb, corruptReason);
        } catch (err) {
          log.error(`[IDB_CORRUPTION] subscriber threw handling cleared corruption for ${key.slice(-8)}:`, err);
        }
      }
    });
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
}

/** Test-only: reset all module state between test cases. */
export function __resetIdbCorruptionTrackerForTests(): void {
  idbLoading.clear();
  subscribers.clear();
}
